import { useMemo, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Button, EditIconSmall, Heading, NakedButton, Tabs, Text } from '@frontend/design-system';
import { useDynamicFormStore } from '../store';
import { DynamicFormSection } from '../types';
import {
  reviewActionsStyle,
  reviewContainerStyle,
  reviewEditButtonStyle,
  reviewFieldsGridStyle,
  reviewSectionHeaderStyle,
  reviewSectionStyle,
  reviewSectionTitleStyle,
} from './form-review.styles';
import { ReviewField } from './review-field';

interface FormReviewGroup {
  formId: string;
  formTitle: string;
  sections: DynamicFormSection[];
}

export interface FormReviewProps {
  /** Inline editing isn't supported here, so Edit sends the patient back to the section's form instead. */
  onEdit: (sectionIndex: number) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
}

const groupSectionsByForm = (sections: DynamicFormSection[]): FormReviewGroup[] => {
  const groups: FormReviewGroup[] = [];
  const groupsById = new Map<string, FormReviewGroup>();

  sections.forEach((section) => {
    let group = groupsById.get(section.formId);
    if (!group) {
      group = { formId: section.formId, formTitle: section.formTitle, sections: [] };
      groupsById.set(section.formId, group);
      groups.push(group);
    }
    group.sections.push(section);
  });

  return groups;
};

export const FormReview = ({ onEdit, onSubmit, isSubmitting }: FormReviewProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const { formSections, formFieldsMap, attachments } = useDynamicFormStore([
    'formSections',
    'formFieldsMap',
    'attachments',
  ]);
  const [activeFormId, setActiveFormId] = useState<string>();

  const formGroups = useMemo(() => groupSectionsByForm(formSections), [formSections]);
  const activeGroup = formGroups.find((group) => group.formId === activeFormId) ?? formGroups[0];

  const renderGroupSections = (group: FormReviewGroup) =>
    group.sections.map((section) => {
      const sectionFields = section.virtualFieldIds
        .map((fieldKey) => formFieldsMap[fieldKey])
        .filter((field): field is NonNullable<typeof field> => !!field && !field.hidden);
      const sectionIndex = formSections.indexOf(section);

      return (
        <section key={section.id} css={reviewSectionStyle}>
          <header css={reviewSectionHeaderStyle}>
            <Heading level={3} css={reviewSectionTitleStyle}>
              {section.title}
            </Heading>
            <NakedButton type='button' onClick={() => onEdit(sectionIndex)} css={reviewEditButtonStyle}>
              <EditIconSmall />
              {t('Edit')}
            </NakedButton>
          </header>

          <div css={reviewFieldsGridStyle}>
            {sectionFields.map((field) => (
              <ReviewField key={field.key} field={field} attachments={attachments} />
            ))}
          </div>
        </section>
      );
    });

  return (
    <div css={reviewContainerStyle}>
      <div>
        <Heading level={2}>{t('Review your information')}</Heading>
        <Text color='light'>{t('Please review your answers below. Click edit to make changes to a section.')}</Text>
      </div>

      {formGroups.length > 1 ? (
        <Tabs controlledTab={activeGroup?.formId} onChange={setActiveFormId}>
          <Tabs.Bar>
            {formGroups.map((group) => (
              <Tabs.Tab key={group.formId} id={group.formId}>
                {group.formTitle}
              </Tabs.Tab>
            ))}
          </Tabs.Bar>
          {formGroups.map((group) => (
            <Tabs.Panel key={group.formId} controller={group.formId} keepMounted={false}>
              {renderGroupSections(group)}
            </Tabs.Panel>
          ))}
        </Tabs>
      ) : (
        activeGroup && renderGroupSections(activeGroup)
      )}

      <div css={reviewActionsStyle}>
        <Button type='button' onClick={onSubmit} loading={isSubmitting}>
          {t('Submit')}
        </Button>
      </div>
    </div>
  );
};
